import React, { useCallback, useEffect, useState } from "react";
import projectData from "../components/projectdata.js";
import "./portfolio.css";
import { IoMdClose } from "react-icons/io";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

const serviceOptions = [
  { value: "hood", label: "ติดตั้งระบบดูดอากาศในห้องครัว" },
  { value: "gas", label: "ติดตั้งระบบแก๊สและระบบความปลอดภัย" },
  { value: "goods", label: "ผลิตและนำเข้าเครื่องครัวครบวงจร" },
  { value: "fab", label: "งานผลิตสแตนเลสและเหล็กตามแบบ" },
];

const serviceTypes = Object.fromEntries(
  serviceOptions.map(({ value, label }) => [value, label])
);

function Portfolio() {
  const [selectedServices, setSelectedServices] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const toggleServiceType = (service) => {
    setSelectedServices((selected) =>
      selected.includes(service)
        ? selected.filter((item) => item !== service)
        : [...selected, service]
    );
  };

  const filteredProjects = projectData.filter((project) =>
    selectedServices.length === 0
      ? true
      : selectedServices.some((service) =>
          project["service-type"].includes(service)
        )
  );

  const openAlbum = (project) => {
    setCurrentProject(project);
    setCurrentIndex(0);
  };

  const closeAlbum = useCallback(() => {
    setCurrentProject(null);
    setCurrentIndex(0);
  }, []);

  const nextSlide = useCallback(() => {
    if (!currentProject) return;
    setCurrentIndex((index) => (index + 1) % currentProject.images.length);
  }, [currentProject]);

  const prevSlide = useCallback(() => {
    if (!currentProject) return;
    setCurrentIndex((index) =>
      index === 0 ? currentProject.images.length - 1 : index - 1
    );
  }, [currentProject]);

  useEffect(() => {
    if (!currentProject) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeAlbum();
      if (event.key === "ArrowRight") nextSlide();
      if (event.key === "ArrowLeft") prevSlide();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentProject, closeAlbum, nextSlide, prevSlide]);

  return (
    <div className="portfolio-page">
      <div className="portfolio-title-row">
        <div>
          <p className="portfolio-eyebrow">POWER BUNCH PROJECTS</p>
          <h1>ผลงานตัวอย่าง</h1>
        </div>
        <p className="portfolio-count">{filteredProjects.length} ผลงาน</p>
      </div>

      <div className="portfolio-grid-container">
        <aside className="filter" aria-label="กรองประเภทบริการ">
          <div className="filter-heading">
            <h3>ประเภทบริการ</h3>
            {selectedServices.length > 0 && (
              <button type="button" onClick={() => setSelectedServices([])}>
                ล้างตัวกรอง
              </button>
            )}
          </div>

          {serviceOptions.map(({ value, label }) => (
            <label key={value} className={selectedServices.includes(value) ? "selected" : ""}>
              <input
                type="checkbox"
                value={value}
                onChange={() => toggleServiceType(value)}
                checked={selectedServices.includes(value)}
              />
              <span>{label}</span>
            </label>
          ))}
        </aside>

        <div className="portfolio-list" aria-live="polite">
          {filteredProjects.map((project, index) => (
            <article key={`${project["project-name"]}-${index}`} className="portfolio-project-container">
              <button
                type="button"
                className="project-cover-button"
                onClick={() => openAlbum(project)}
                aria-label={`เปิดอัลบั้ม ${project["project-name"]}`}
              >
                <img src={project.images[0]} alt={project["project-name"]} />
                <span className="project-image-count">{project.images.length} รูป</span>
                <span className="project-view-overlay">เปิดดูอัลบั้ม <FaArrowRight /></span>
              </button>

              <div className="project-card-body">
                <div className="badge-container">
                  {project["service-type"].map((type) => (
                    <span key={type} className="badge">{serviceTypes[type]}</span>
                  ))}
                </div>
                <h2>{project["project-name"]}</h2>
                <p>{project.description}</p>
                <button type="button" className="more-btn" onClick={() => openAlbum(project)}>
                  ดูรูปภาพเพิ่มเติม <FaArrowRight />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {currentProject && (
        <div className="modal" role="presentation" onMouseDown={closeAlbum}>
          <section
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-label={`อัลบั้ม ${currentProject["project-name"]}`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <p className="album-label">PROJECT ALBUM</p>
                <h3>{currentProject["project-name"]}</h3>
              </div>
              <button type="button" onClick={closeAlbum} className="close-btn" aria-label="ปิดอัลบั้ม">
                <IoMdClose />
              </button>
            </div>

            <div className="slider">
              <img
                key={currentProject.images[currentIndex]}
                src={currentProject.images[currentIndex]}
                alt={`${currentProject["project-name"]} รูปที่ ${currentIndex + 1}`}
              />
              {currentProject.images.length > 1 && (
                <>
                  <button type="button" className="prev" onClick={prevSlide} aria-label="รูปก่อนหน้า">
                    <FaArrowLeft />
                  </button>
                  <button type="button" className="next" onClick={nextSlide} aria-label="รูปถัดไป">
                    <FaArrowRight />
                  </button>
                </>
              )}
              <span className="image-length">
                {currentIndex + 1} / {currentProject.images.length}
              </span>
            </div>

            {currentProject.images.length > 1 && (
              <div className="album-thumbnails" aria-label="เลือกรูปภาพ">
                {currentProject.images.map((image, index) => (
                  <button
                    type="button"
                    key={`${image}-${index}`}
                    className={index === currentIndex ? "active" : ""}
                    onClick={() => setCurrentIndex(index)}
                    aria-label={`ดูรูปที่ ${index + 1}`}
                    aria-current={index === currentIndex ? "true" : undefined}
                  >
                    <img src={image} alt="" />
                  </button>
                ))}
              </div>
            )}

            <p className="album-description">{currentProject.description}</p>
          </section>
        </div>
      )}
    </div>
  );
}

export default Portfolio;
